import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changeAdminPassword } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Lock, Shield, Loader2, Eye, EyeOff, Check, X } from "lucide-react";
import GoldShieldLogo from "@/components/GoldShieldLogo";

export default function AdminChangePasswordPage() {
  const navigate = useNavigate();
  const { admin, checkAuth } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Password requirements validation
  const requirements = [
    { label: "Minim 12 caractere", met: newPassword.length >= 12 },
    { label: "O literă mare", met: /[A-Z]/.test(newPassword) },
    { label: "O literă mică", met: /[a-z]/.test(newPassword) },
    { label: "O cifră", met: /[0-9]/.test(newPassword) },
    { label: "Un simbol (!@#$%^&*...)", met: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword) },
  ];

  const allRequirementsMet = requirements.every(r => r.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError("Parola nu îndeplinește toate cerințele");
      return;
    }

    if (!passwordsMatch) {
      setError("Parolele nu se potrivesc");
      return;
    }

    setLoading(true);

    try {
      await changeAdminPassword(currentPassword, newPassword, confirmPassword);
      toast.success("Parola a fost schimbată cu succes!");
      
      // Refresh auth state to update must_change_password flag
      await checkAuth();
      
      // Navigate to admin dashboard
      navigate("/admin", { replace: true });
    } catch (err) {
      console.error("Password change error:", err);
      const message = err.response?.data?.detail || "Eroare la schimbarea parolei";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex justify-center mb-4">
            <GoldShieldLogo className="w-16 h-20" />
          </div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white">
            RTS EXPRESS
          </h1>
          <p className="text-slate-400 text-sm mt-1">Admin Panel</p>
        </div>

        {/* Change Password Form */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-8">
          {/* Security Notice */}
          <div className="flex items-center gap-3 mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
            <Shield className="w-6 h-6 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-amber-500 font-bold text-sm">Schimbare Parolă Obligatorie</p>
              <p className="text-amber-400/80 text-xs mt-1">
                Pentru securitate, trebuie să schimbați parola implicită înainte de a accesa panoul admin.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#22c55e]" />
            <h2 className="text-xl font-bold text-white">Schimbare Parolă</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Parola Curentă
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10 pr-10 bg-slate-900 border-slate-800 text-white focus:ring-[#22c55e] focus:border-[#22c55e]"
                  placeholder="Introduceți parola curentă"
                  required
                  autoComplete="current-password"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Parolă Nouă
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10 bg-slate-900 border-slate-800 text-white focus:ring-[#22c55e] focus:border-[#22c55e]"
                  placeholder="Introduceți parola nouă"
                  required
                  autoComplete="new-password"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-900/50 p-4 rounded-sm border border-slate-800">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">
                Cerințe Parolă
              </p>
              <div className="space-y-2">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {req.met ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <X className="w-4 h-4 text-slate-600" />
                    )}
                    <span className={`text-sm ${req.met ? 'text-green-500' : 'text-slate-500'}`}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-400">
                Confirmă Parola Nouă
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 bg-slate-900 border-slate-800 text-white focus:ring-[#22c55e] focus:border-[#22c55e]"
                  placeholder="Confirmați parola nouă"
                  required
                  autoComplete="new-password"
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && (
                <div className="flex items-center gap-2 mt-1">
                  {passwordsMatch ? (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-500">Parolele se potrivesc</span>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-red-500">Parolele nu se potrivesc</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !allRequirementsMet || !passwordsMatch || !currentPassword}
              className="w-full bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-bold"
              data-testid="change-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Se schimbă parola...
                </>
              ) : (
                "Schimbă Parola"
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-6">
          RTS EXPRESS LTD © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
